import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild, inject } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';

import { resolveSelfServiceApiUrl } from '../../self-service-visualization/shared/self-service-api.utils';

/** One action button inside a popup `buttons` block. */
export interface DvmPopupButton {
  label: string;
  /** close = dismiss the popup; link = open `url` in a new tab; navigate =
   *  emit `targetDashboard` so the parent routes to that published dashboard. */
  action: 'close' | 'link' | 'navigate';
  url?: string;
  targetDashboard?: string;
  style?: 'primary' | 'secondary' | 'link';
}

/** apiTable block: one field pulled out of a response group and shown in the
 *  group's header strip (e.g. "Updated By: aditya"). */
export interface DvmPopupMetaField {
  field: string;
  label: string;
}

/** apiTable block: one rendered column — `color` tints the cell values (named
 *  color from the builder's picker, or any CSS color). */
export interface DvmPopupApiColumn {
  field: string;
  label: string;
  color?: string;
}

/** One content block of the popup body, rendered top-to-bottom. */
export interface DvmPopupBlock {
  type: 'heading' | 'text' | 'keyValue' | 'table' | 'apiTable' | 'buttons' | 'divider';
  /** heading + text blocks. */
  text?: string;
  /** heading only: h1 | h2 | h3. */
  level?: string;
  align?: string;
  /** keyValue block. */
  pairs?: Array<{ label: string; value: string }>;
  /** table block — rows are SNAPSHOTTED at build time (client-side, from the
   *  builder's dataset rows cache), so rendering needs no backend call. */
  dataset?: string;
  columns?: string[];
  maxRows?: number;
  rows?: Array<Record<string, unknown>>;
  /** apiTable block — rows are FETCHED from `url` when the popup opens.
   *  Optional `groupsPath` renders the response as collapsible groups (audit /
   *  change-history style), each with `metaFields` in its header and its own
   *  rows table; without it the response renders as one flat table. */
  url?: string;
  method?: string;
  body?: string;
  rowsPath?: string;
  groupsPath?: string;
  groupRowsField?: string;
  metaFields?: DvmPopupMetaField[];
  apiColumns?: DvmPopupApiColumn[];
  refreshOnOpen?: boolean;
  /** buttons block. */
  buttons?: DvmPopupButton[];
}

/** Runtime state of one apiTable block's fetch. */
interface DvmPopupApiState {
  loading: boolean;
  error: string;
  groups: Array<{
    meta: Array<{ label: string; value: string }>;
    rows: Array<Record<string, unknown>>;
    expanded: boolean;
  }>;
}

/**
 * A configurable popup tile for the DVM layout builder + published dashboard.
 * Renders a TRIGGER (icon / button / tile — `triggerMode`) and, on click, a
 * self-contained overlay dialog whose body is a list of content blocks
 * (heading / text / key-value / table / buttons / divider). Everything is
 * `[data]`-driven (the tile's widgetData), so the builder preview and the
 * published view render identically with no extra wiring. `close`/`link`
 * button actions are handled internally; `navigate` is EMITTED so the parent
 * owns routing (it differs between builder and published view).
 */
@Component({
  selector: 'app-dvm-popup',
  standalone: true,
  imports: [CommonModule, MatIconModule],
  templateUrl: './dvm-popup.component.html',
  styleUrl: './dvm-popup.component.scss',
})
export class DvmPopupComponent implements OnChanges, OnDestroy {
  private readonly http = inject(HttpClient);

  /** The tile's widgetData: trigger + behaviour + style keys and `blocks`. */
  @Input() data: Record<string, unknown> = {};

  /** Per-block fetch state for apiTable blocks, keyed by block index. */
  apiState: Record<number, DvmPopupApiState> = {};

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['data']) return;
    // New widgetData (regenerated tile / live form edit) — drop cached API
    // results so the next open refetches with the current config; refetch at
    // once when the dialog is already showing.
    this.apiState = {};
    if (this.open) this.fetchApiBlocks();
  }

  /** A `navigate` button was clicked — value is the target dashboard slug. */
  @Output() navigateTo = new EventEmitter<string>();

  /** The overlay root — reparented to <body> while open (see openPopup). */
  @ViewChild('overlayRoot') overlayRoot?: ElementRef<HTMLElement>;

  open = false;

  get triggerMode(): string {
    const mode = String(this.data?.['triggerMode'] ?? 'button');
    return mode === 'icon' || mode === 'tile' ? mode : 'button';
  }
  get triggerIcon(): string {
    return String(this.data?.['triggerIcon'] ?? '').trim() || 'info';
  }
  get triggerLabel(): string {
    return String(this.data?.['triggerLabel'] ?? '').trim() || this.title || 'Details';
  }
  get triggerHint(): string {
    return String(this.data?.['triggerHint'] ?? '').trim();
  }
  get title(): string {
    return String(this.data?.['title'] ?? '').trim();
  }
  get popupSize(): string {
    return String(this.data?.['popupSize'] ?? 'medium');
  }
  get popupPosition(): string {
    return String(this.data?.['popupPosition'] ?? 'center') === 'drawer' ? 'drawer' : 'center';
  }
  get closeLabel(): string {
    return String(this.data?.['closeLabel'] ?? 'Close');
  }
  get dismissOnBackdrop(): boolean {
    return this.data?.['dismissOnBackdrop'] !== false;
  }
  get blocks(): DvmPopupBlock[] {
    const raw = this.data?.['blocks'];
    return Array.isArray(raw) ? (raw as DvmPopupBlock[]) : [];
  }

  /** Popup-wide font overrides from the build form's Formatting accordion. */
  private get fontFamily(): string {
    return String(this.data?.['fontFamily'] ?? '').trim();
  }
  private get fontSize(): number {
    const size = Number(this.data?.['fontSize']);
    return Number.isFinite(size) && size > 0 ? size : 0;
  }

  /** Trigger styling (build-form "Formatting" accordion; empty = theme default). */
  get triggerStyles(): Record<string, string> {
    const styles: Record<string, string> = {};
    const bg = String(this.data?.['triggerBg'] ?? '').trim();
    const text = String(this.data?.['triggerText'] ?? '').trim();
    const radius = Number(this.data?.['triggerRadius']);
    if (bg) styles['background'] = bg;
    if (text) styles['color'] = text;
    if (Number.isFinite(radius) && radius >= 0) styles['border-radius'] = `${radius}px`;
    if (this.fontFamily) styles['font-family'] = this.fontFamily;
    if (this.fontSize) styles['font-size'] = `${this.fontSize}px`;
    return styles;
  }

  /** Font overrides applied to the opened dialog (header/body/footer inherit). */
  get dialogStyles(): Record<string, string> {
    const styles: Record<string, string> = {};
    if (this.fontFamily) styles['font-family'] = this.fontFamily;
    if (this.fontSize) styles['font-size'] = `${this.fontSize}px`;
    return styles;
  }

  /** Dialog title-bar colors (Formatting accordion's Header background/text —
   *  the popup's equivalent of the other components' card-header keys). */
  get headerStyles(): Record<string, string> {
    const styles: Record<string, string> = {};
    const bg = String(this.data?.['headerBg'] ?? '').trim();
    const text = String(this.data?.['headerText'] ?? '').trim();
    if (bg) styles['background'] = bg;
    if (text) styles['color'] = text;
    return styles;
  }
  get headerTextColor(): string | null {
    const text = String(this.data?.['headerText'] ?? '').trim();
    return text || null;
  }

  /** Optional dialog style overrides (Formatting accordion) exposed as CSS
   *  custom properties on the dialog — null keeps the theme default. */
  styleColor(key: string): string | null {
    const value = String(this.data?.[key] ?? '').trim();
    return value || null;
  }
  stylePx(key: string): string | null {
    const value = Number(this.data?.[key]);
    return Number.isFinite(value) && value >= 0 ? `${value}px` : null;
  }

  openPopup(): void {
    this.open = true;
    this.fetchApiBlocks();
    // position:fixed resolves against the nearest TRANSFORMED ancestor, not
    // the viewport — and dashboard tiles (drag/resize/animation) can carry a
    // transform, which trapped the dialog inside the card. Reparent the
    // overlay node to <body> once *ngIf has rendered it; Angular's bindings
    // and event listeners keep working on the moved node, and closing (the
    // *ngIf) removes it from wherever it lives.
    setTimeout(() => {
      const el = this.overlayRoot?.nativeElement;
      if (el && el.parentElement !== document.body) document.body.appendChild(el);
    });
  }
  closePopup(): void {
    this.open = false;
  }
  ngOnDestroy(): void {
    // Destroyed while open (route change) — make sure the body-level node goes.
    const el = this.overlayRoot?.nativeElement;
    if (el && el.parentElement === document.body) el.remove();
  }
  onBackdropClick(): void {
    if (this.dismissOnBackdrop) this.closePopup();
  }
  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.open) this.closePopup();
  }

  onButtonClick(button: DvmPopupButton): void {
    const action = String(button?.action ?? 'close');
    if (action === 'link') {
      const url = String(button?.url ?? '').trim();
      if (url) window.open(url, '_blank', 'noopener');
      return;
    }
    if (action === 'navigate') {
      const slug = String(button?.targetDashboard ?? '').trim();
      this.closePopup();
      if (slug) this.navigateTo.emit(slug);
      return;
    }
    this.closePopup();
  }

  headingTag(block: DvmPopupBlock): string {
    const level = String(block?.level ?? 'h2').toLowerCase();
    return level === 'h1' || level === 'h3' ? level : 'h2';
  }

  /** Columns a table block renders: the picked list, else the first row's keys. */
  tableColumns(block: DvmPopupBlock): string[] {
    if (Array.isArray(block?.columns) && block.columns.length) return block.columns;
    const first = block?.rows?.[0];
    return first ? Object.keys(first) : [];
  }
  tableRows(block: DvmPopupBlock): Array<Record<string, unknown>> {
    const rows = Array.isArray(block?.rows) ? block.rows : [];
    const max = Number(block?.maxRows);
    return Number.isFinite(max) && max > 0 ? rows.slice(0, max) : rows;
  }
  formatCell(value: unknown): string {
    if (value == null) return '';
    if (typeof value === 'number') return Number.isInteger(value) ? value.toLocaleString() : value.toLocaleString(undefined, { maximumFractionDigits: 2 });
    return String(value);
  }

  trackByIndex(index: number): number {
    return index;
  }

  // ── apiTable blocks — fetch on open, parse flat or grouped ────────────────

  /** Fire the API call for every apiTable block that needs one. Results are
   *  cached per block; `refreshOnOpen` refetches on every open. */
  private fetchApiBlocks(): void {
    this.blocks.forEach((block, index) => {
      if (block.type !== 'apiTable') return;
      const url = String(block.url ?? '').trim();
      if (!url) return;
      const existing = this.apiState[index];
      if (existing && !existing.error && !block.refreshOnOpen) return;
      this.apiState = { ...this.apiState, [index]: { loading: true, error: '', groups: [] } };
      const resolved = resolveSelfServiceApiUrl(url);
      const method = String(block.method ?? 'GET').toUpperCase();
      let body: unknown = {};
      if (block.body && String(block.body).trim()) {
        try { body = JSON.parse(String(block.body)); } catch { body = String(block.body); }
      }
      const request$ = method === 'POST'
        ? this.http.post<unknown>(resolved, body)
        : this.http.get<unknown>(resolved);
      request$.subscribe({
        next: (response) => {
          this.apiState = { ...this.apiState, [index]: { loading: false, error: '', groups: this.parseApiResponse(block, response) } };
        },
        error: () => {
          this.apiState = { ...this.apiState, [index]: { loading: false, error: 'Could not load data.', groups: [] } };
        },
      });
    });
  }

  /** Walk a dot-path ("data.history") into a response object. */
  private resolvePath(source: unknown, path: string): unknown {
    if (!path) return undefined;
    return path.split('.').reduce<unknown>((acc, key) => (acc == null ? undefined : (acc as Record<string, unknown>)[key]), source);
  }

  /** The first array found: the value itself, or a common envelope key. */
  private firstArray(source: unknown, preferredKeys: string[] = []): Array<Record<string, unknown>> {
    if (Array.isArray(source)) return source as Array<Record<string, unknown>>;
    const record = (source && typeof source === 'object') ? source as Record<string, unknown> : null;
    for (const key of [...preferredKeys, 'rows', 'data', 'Data', 'results', 'changes', 'history', 'items']) {
      if (!key) continue;
      const candidate = record?.[key];
      if (Array.isArray(candidate)) return candidate as Array<Record<string, unknown>>;
    }
    return [];
  }

  /** Response → group list. `groupsPath` set: one collapsible group per entry
   *  (meta header from `metaFields`, rows from `groupRowsField`/common keys).
   *  Unset: a single headerless group = one flat table. */
  private parseApiResponse(block: DvmPopupBlock, response: unknown): DvmPopupApiState['groups'] {
    const max = Number(block.maxRows);
    const cap = (rows: Array<Record<string, unknown>>) => (Number.isFinite(max) && max > 0 ? rows.slice(0, max) : rows);
    const groupsPath = String(block.groupsPath ?? '').trim();
    if (groupsPath) {
      const rawGroups = this.resolvePath(response, groupsPath);
      const list = Array.isArray(rawGroups) ? rawGroups as Array<Record<string, unknown>> : [];
      return list.map((group) => ({
        meta: (block.metaFields ?? [])
          .filter((meta) => meta.field)
          .map((meta) => ({ label: meta.label || meta.field, value: String(this.resolvePath(group, meta.field) ?? '') })),
        rows: cap(this.firstArray(group, [String(block.groupRowsField ?? '').trim()])),
        expanded: true,
      }));
    }
    const rowsPath = String(block.rowsPath ?? '').trim();
    const rows = rowsPath
      ? (Array.isArray(this.resolvePath(response, rowsPath)) ? this.resolvePath(response, rowsPath) as Array<Record<string, unknown>> : [])
      : this.firstArray(response);
    return [{ meta: [], rows: cap(rows), expanded: true }];
  }

  /** Columns an apiTable renders: configured ones, else the first row's keys. */
  apiTableColumns(block: DvmPopupBlock, group: DvmPopupApiState['groups'][number]): DvmPopupApiColumn[] {
    const configured = (block.apiColumns ?? []).filter((column) => column.field);
    if (configured.length) return configured;
    const first = group.rows[0];
    return first ? Object.keys(first).map((key) => ({ field: key, label: key })) : [];
  }

  /** Legend entries — only the columns given a value color. */
  legendColumns(block: DvmPopupBlock): DvmPopupApiColumn[] {
    return (block.apiColumns ?? []).filter((column) => column.field && column.color);
  }

  private static readonly NAMED_COLORS: Record<string, string> = {
    red: '#c0392b',
    green: '#1e8e3e',
    blue: '#1f6feb',
    orange: '#d97706',
  };

  /** Named color → hex; any other non-empty value passes through as CSS. */
  colorFor(color?: string): string | null {
    const value = String(color ?? '').trim();
    if (!value) return null;
    return DvmPopupComponent.NAMED_COLORS[value.toLowerCase()] ?? value;
  }
}
